import React, { useState } from 'react';
import styled from 'styled-components';
import { Modal, Button, TextInput } from '@kardume/ui';
import { authService } from '@kardume/services';
import { useNotification } from '@kardume/hooks';

const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Hint = styled.p`
  margin: 0;
  font-size: 0.85rem;
  color: ${(p) => p.theme.textMuted};
`;

interface ChangePasswordModalProps {
  email: string;
  currentPassword: string;
  onClose: () => void;
  onSuccess: () => void;
}

/** Mandatory password change on first access (authStatus PASSWORD_CHANGE_REQUIRED). */
const ChangePasswordModal: React.FC<ChangePasswordModalProps> = ({ email, currentPassword, onClose, onSuccess }) => {
  const { showNotification } = useNotification();
  const [newPassword, setNewPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);

  const submit = async (): Promise<void> => {
    if (saving) return;
    if (newPassword.length < 8) {
      showNotification('A nova senha deve ter ao menos 8 caracteres.', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showNotification('A confirmação não confere com a nova senha.', 'error');
      return;
    }
    setSaving(true);
    try {
      await authService.changePassword({ email, currentPassword, newPassword, confirmPassword });
      await authService.login({ email, password: newPassword });
      showNotification('Senha alterada com sucesso.', 'success');
      onSuccess();
    } catch (err) {
      showNotification((err as Error)?.message || 'Não foi possível alterar a senha.', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Redefinir senha"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={submit} isLoading={saving}>
            Salvar nova senha
          </Button>
        </>
      }
    >
      <Form>
        <Hint>Por segurança, defina uma nova senha para concluir o acesso.</Hint>
        <TextInput label="Nova senha" type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
        <TextInput
          label="Confirmar nova senha"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
        />
      </Form>
    </Modal>
  );
};

export default ChangePasswordModal;
